import {
  createContext,
  ReactNode,
  SetStateAction,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { SidebarState } from '~/layout/enum/SidebarState';
import { useContext } from '@/hooks/useContext';
import { persistentStorage } from '@/lib/storage';
import {
  SharedValue,
  useSharedValue,
  withTiming,
} from 'react-native-reanimated';
import { sidebarConfig } from '~/layout/defs/sidebarConfig';

export interface SidebarLayoutContext {
  state: SidebarState;
  openSidebar: () => void;
  closeSidebar: () => void;
  toggleSidebar: () => void;
  expandedState: SharedValue<number>;
  itemsDetailsVisibleState: SharedValue<number>;
}

const ctx = createContext<SidebarLayoutContext | undefined>(undefined);
ctx.displayName = 'SidebarLayout';

export interface SidebarLayoutProviderProps {
  children?: ReactNode;
}

export const SidebarLayoutProvider = ({
  children,
}: SidebarLayoutProviderProps) => {
  const [state, setState] = useState(SidebarState.COLLAPSED);
  const setStateInternal = (state: SetStateAction<SidebarState>) =>
    setState((oldState) => {
      const newState = typeof state === 'function' ? state(oldState) : state;
      void persistentStorage.set(
        'sidebarCollapsed',
        newState === SidebarState.COLLAPSED,
      );
      return newState;
    });

  const expandedState = useSharedValue(state === SidebarState.EXPANDED ? 1 : 0);
  const itemsDetailsVisibleState = useSharedValue(
    state === SidebarState.EXPANDED ? 1 : 0,
  );
  const collapse = () => {
    itemsDetailsVisibleState.value = withTiming(
      0,
      { duration: sidebarConfig.durations.itemsVisibility },
      (finished) => {
        if (finished) {
          expandedState.value = withTiming(0, {
            duration: sidebarConfig.durations.expand,
          });
        }
      },
    );
  };

  const expand = () => {
    expandedState.value = withTiming(
      1,
      { duration: sidebarConfig.durations.expand },
      (finished) => {
        if (finished) {
          itemsDetailsVisibleState.value = withTiming(1, {
            duration: sidebarConfig.durations.itemsVisibility,
          });
        }
      },
    );
  };
  useEffect(() => {
    if (state === SidebarState.COLLAPSED) {
      collapse();
    } else {
      expand();
    }
  }, [state]);

  const value: SidebarLayoutContext = useMemo(
    () => ({
      state,
      expandedState,
      itemsDetailsVisibleState,
      openSidebar: () => setStateInternal(SidebarState.EXPANDED),
      closeSidebar: () => setStateInternal(SidebarState.COLLAPSED),
      toggleSidebar: () =>
        setStateInternal((state) =>
          state === SidebarState.EXPANDED
            ? SidebarState.COLLAPSED
            : SidebarState.EXPANDED,
        ),
    }),
    [state],
  );
  useEffect(() => {
    persistentStorage.get('sidebarCollapsed').then((collapsed) => {
      const open = !collapsed;
      setState(open ? SidebarState.EXPANDED : SidebarState.COLLAPSED);
    });
  }, []);
  return <ctx.Provider value={value}>{children}</ctx.Provider>;
};

export const useSidebarLayout = () => useContext(ctx);
